import { useNode } from "@craftjs/core";
import { Paper, Stack, TextField, Switch, FormControl, InputLabel, MenuItem, Select, Slider, Divider } from "@mui/material";
import useResponsiveValues, { ResponsiveValue } from "@/components/editor/hooks/useResponsiveValues";
import SettingCard from "@/components/SettingCard";
import ClassSettings from "./ClassSettings";
import { ReactNode, useContext, useMemo } from "react";
import SocialMediaIcon from "@/components/SocialMediaIcon/SocialMediaIcon";
import { OrganizationProvider } from "@/features/websites/components/OrganizationProvider";
import NewColorPicker from "@/components/editor/settings/ColorSettings";

export interface Profile {
  id: string;
  label: string;
  icon: ReactNode;
  url: string;
  alias: string;
}

type SocialMediaDirection = "row" | "column";
type SocialMediaVariant = "filled" | "outlined";
type Position = "right" | "left" | "bottom" | "hidden";

export interface SocialMedia {
  color?: string | ResponsiveValue<string>;
  strokeWidth: number | ResponsiveValue<number>;
  spacing: number | ResponsiveValue<number>;
  profiles: Record<string, Profile>;
  direction: SocialMediaDirection | ResponsiveValue<SocialMediaDirection>;
  variant: SocialMediaVariant | ResponsiveValue<SocialMediaVariant>;
  labelPosition: Position | ResponsiveValue<Position>;
}

const SocialSettings = () => {
  const responsive = useResponsiveValues();
  const organization = useContext(OrganizationProvider);

  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));

  const initialProfileList = useMemo(() => {
    const draft = {
      facebook: {
        id: "facebook",
        label: "Facebook",
        url: "https://facebook.com/",
        alias: "",
      },
      instagram: {
        id: "instagram",
        label: "Instagram",
        url: "https://instagram.com/",
        alias: "",
      },
      x: {
        id: "x",
        label: "X",
        url: "https://x.com/",
        alias: "",
      },
      whatsapp: {
        id: "whatsapp",
        label: "Whatsapp",
        url: "https://wa.me/",
        alias: "",
      },
    };

    if (!organization) return draft;

    if (organization?.facebook) {
      draft["facebook"].alias = organization.facebook;
    }
    if (organization?.instagram) {
      draft["instagram"].alias = organization.instagram;
    }
    if (organization?.x) {
      draft["x"].alias = organization.x;
    }
    if (organization?.whatsapp) {
      draft["whatsapp"].alias = "https://wa.me/" + organization.whatsapp.toString().replace("-", ""); // TODO improve with regex to remove country reference
    }

    return draft;
  }, [organization]);

  const handleToogle = (e, profile) => {
    const isActive = e.target.checked;
    const profiles = props?.profiles ?? {};
    const isInUse = !!profiles[profile.id];

    const draft = { ...profiles };

    if (!isActive && isInUse) {
      delete draft[profile.id];
    } else {
      draft[profile.id] = profile;
    }

    setProp((props: SocialMedia) => {
      props.profiles = draft;
    });
  };

  const handleChangeUrl = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target;

    // if (!props.profiles[name]) return;

    setProp((props: SocialMedia) => {
      props.profiles[name].alias = value;
    });
  };

  return (
    <Stack spacing={2}>
      <SettingCard title="Clase Css">
        <ClassSettings />
      </SettingCard>
      <SettingCard title="Distribución">
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="social-media-setting-direction">Dirección</InputLabel>
              <Select
                label="Dirección"
                labelId="social-media-setting-direction"
                value={props?.direction ?? "column"}
                onChange={(e) => {
                  setProp((props: SocialMedia) => {
                    props.direction = e.target.value as SocialMedia["direction"];
                  });
                }}
              >
                <MenuItem value="column">Vertical</MenuItem>
                <MenuItem value="row">Horizontal</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="social-media-setting-label-position">Posición de etiqueta</InputLabel>
              <Select
                label="Posición de etiqueta"
                labelId="social-media-setting-label-position"
                value={props?.labelPosition ?? "right"}
                onChange={(e) => {
                  setProp((props: SocialMedia) => {
                    props.labelPosition = e.target.value as SocialMedia["labelPosition"];
                  });
                }}
              >
                <MenuItem value="right">Derecha</MenuItem>
                <MenuItem value="left">Izquierda</MenuItem>
                <MenuItem value="bottom">Abajo</MenuItem>
                <Divider />
                <MenuItem value="hidden">Oculto</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Slider
            step={1}
            marks
            min={1}
            max={20}
            value={Number(responsive.getValue(props.spacing)) ?? 0}
            onChange={(_, value) =>
              setProp((props: SocialMedia) => {
                props.spacing = responsive.setValue(props.spacing, value);
                return props;
              })
            }
          />
        </Stack>
      </SettingCard>
      <SettingCard title="Estilo">
        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel id="social-media-setting-variant">Variante</InputLabel>
            <Select
              label="Variante"
              labelId="social-media-setting-variant"
              value={props?.variant ?? "filled"}
              onChange={(e) => {
                setProp((props: SocialMedia) => {
                  props.variant = e.target.value as SocialMedia["variant"];
                });
              }}
            >
              <MenuItem value="filled">Relleno</MenuItem>
              <MenuItem value="outlined">Contornos</MenuItem>
            </Select>
          </FormControl>
          <Slider
            step={1}
            marks
            min={1}
            max={4}
            value={Number(responsive.getValue(props.strokeWidth)) ?? 0}
            onChange={(_, value) =>
              setProp((props: SocialMedia) => {
                props.strokeWidth = responsive.setValue(props.strokeWidth, value);
                return props;
              })
            }
          />
          <NewColorPicker field="color" />
        </Stack>
      </SettingCard>

      <SettingCard title="Perfiles">
        <Stack spacing={2}>
          {Object.values(initialProfileList).map((profile) => (
            <Paper key={profile.id} sx={{ padding: 2 }}>
              <Stack spacing={2} direction="row" alignItems="center">
                <SocialMediaIcon icon={profile.id} />
                <TextField
                  fullWidth
                  size="small"
                  name={profile.id}
                  value={props?.profiles?.[profile.id]?.alias ?? profile?.alias}
                  disabled={!props?.profiles?.[profile.id]}
                  onChange={handleChangeUrl}
                />
                <Switch onChange={(e) => handleToogle(e, profile)} checked={props?.profiles?.[profile.id]} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      </SettingCard>
    </Stack>
  );
};

export default SocialSettings;
